<template>
  <CollapsibleSection
    :title="section.title"
    :icon="section.icon"
    context="quote"
    :storage-key="section.storageKey"
    :use-grid="false"
    :bare="bare"
  >
    <template v-if="section.requiredSection" #header-extra>
      <span class="req" aria-label="obligatoire">*</span>
    </template>

    <DetailItem
      v-for="field in fields"
      :key="field.key"
      :label="fieldLabel(field)"
      :data-field="field.key"
      :data-invalid="fieldInvalid(field) ? sectionKey : null"
      :class="{ 'bcs-invalid': fieldInvalid(field) }"
      :copyable="false"
    >
      <!-- Le groupe porte role="group" : sans nom, un lecteur d'écran annonce
           « groupe » tout court. Le libellé de DetailItem n'est pas relié au
           groupe (et il est vide quand le titre de section dit la question). -->
      <FormCheckboxGroup
        v-if="field.type === 'checkbox-group'"
        :aria-label="field.label"
        :options="field.options"
        :model-value="modelValue[field.key]"
        :autre-field="Boolean(field.autreKey)"
        :autre-value="field.autreKey ? modelValue[field.autreKey] : ''"
        :disabled="disabled"
        @update:model-value="modelValue[field.key] = $event"
        @update:autre-value="field.autreKey && (modelValue[field.autreKey] = $event)"
      />

      <RadioGroup
        v-else-if="field.type === 'radio'"
        :options="field.options"
        :name="field.key"
        :model-value="modelValue[field.key]"
        :disabled="disabled"
        :error="errors[field.key] || null"
        @update:model-value="modelValue[field.key] = $event"
      />

      <Textarea
        v-else-if="field.type === 'textarea'"
        v-model="modelValue[field.key]"
        :rows="field.rows || 2"
        :placeholder="field.placeholder || ''"
        :disabled="disabled"
        :error="errors[field.key] || null"
      />

      <Input
        v-else
        v-model="modelValue[field.key]"
        :placeholder="field.placeholder || ''"
        :disabled="disabled"
        :error="errors[field.key] || null"
      />

      <!-- Les cases à cocher n'ont pas de message d'erreur à elles, ni la
           précision « Autre » : il se pose ici, sous le champ. -->
      <p v-if="field.type === 'checkbox-group' && errors[field.key]" class="error-text" role="alert">
        {{ errors[field.key] }}
      </p>
      <p v-if="field.autreKey && errors[field.autreKey]" class="error-text" role="alert">
        {{ errors[field.autreKey] }}
      </p>
    </DetailItem>

    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
/**
 * Rend une section commune (objectif / adaptation / accessibilité) dans
 * l'habillage du back-office. Les champs, options, conditions d'affichage et
 * obligations viennent du schéma partagé avec le questionnaire d'organisation :
 * l'étape 1 et l'étape 2 ne peuvent plus proposer des réponses différentes.
 */
import { computed } from 'vue'
import { BESOIN_COMMUN_SECTIONS, visibleFields, isFieldRequired } from '../../composables/questionnaires/besoin-commun-schema.js'
import { CollapsibleSection, DetailItem } from '../data'
import { Input, Textarea, RadioGroup } from '../ui'
import FormCheckboxGroup from '../questionnaire/FormCheckboxGroup.vue'

const props = defineProps({
  sectionKey: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Mode « nu » : le contenu seul, sans carte ni en-tête repliable. La fenêtre
   * « Analyse du besoin » v2 n'affiche qu'une section à la fois et porte
   * elle-même son titre — la carte y ferait un cadre dans un cadre. En mode nu,
   * l'astérisque de section suit le titre, qui n'est plus rendu ici : c'est la
   * fenêtre qui l'annonce.
   * Additif : sans la prop, le rendu est exactement celui d'avant.
   */
  bare: {
    type: Boolean,
    default: false,
  },
  /**
   * Message d'erreur par clé de champ — rempli par l'analyse du besoin quand
   * la confirmation de la section vient d'être refusée. Vide, rien ne change.
   */
  errors: {
    type: Object,
    default: () => ({}),
  },
})

const section = computed(() =>
  BESOIN_COMMUN_SECTIONS.find((s) => s.key === props.sectionKey)
)

const fields = computed(() =>
  section.value ? visibleFields(section.value, props.modelValue) : []
)

/** Le champ, ou sa précision « Autre », est-il en erreur ? */
function fieldInvalid(field) {
  return Boolean(props.errors[field.key] || (field.autreKey && props.errors[field.autreKey]))
}

// Section à question unique (l'objectif professionnel) : le titre de section dit
// déjà la question, un libellé de champ le répéterait.
function fieldLabel(field) {
  if (section.value?.fields.length === 1 && field.label === section.value.title) return ''
  return isFieldRequired(field, props.modelValue) ? `${field.label} *` : field.label
}
</script>

<style scoped>
/* Les cases du groupe et la zone « Autre » passent en rouge, comme un champ
   simple en erreur (`form-control-error`). */
.bcs-invalid :deep(.checkbox-label),
.bcs-invalid :deep(textarea) {
  border-color: var(--color-danger);
}
</style>
